import { useState, useEffect } from 'react';
import { useDebounce } from './useDebounce';

interface ValidationResult {
  isValid: boolean;
  isLoading: boolean;
  error: string | null;
  practitionerName: string | null;
  practitionerId: string | null;
  codeId: string | null;
  discountPercentage: number | null;
}

export const useReferralCodeValidation = (code: string) => {
  const [result, setResult] = useState<ValidationResult>({
    isValid: false,
    isLoading: false,
    error: null,
    practitionerName: null,
    practitionerId: null,
    codeId: null,
    discountPercentage: null,
  });

  // Debounce the code input by 500ms
  const debouncedCode = useDebounce(code, 500);

  useEffect(() => {
    // Don't validate empty codes
    if (!debouncedCode || debouncedCode.trim().length === 0) {
      setResult({
        isValid: false,
        isLoading: false,
        error: null,
        practitionerName: null,
        practitionerId: null,
        codeId: null,
        discountPercentage: null,
      });
      return;
    }

    // Start validation
    setResult(prev => ({ ...prev, isLoading: true, error: null }));

    const validateCode = async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_BACKEND_URL}/referrals/validate-code/${encodeURIComponent(debouncedCode.trim().toUpperCase())}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to validate referral code');
        }

        const data = await response.json();

        setResult({
          isValid: data.valid,
          isLoading: false,
          error: data.valid ? null : data.message,
          practitionerName: data.practitioner_name,
          practitionerId: data.practitioner_id,
          codeId: data.code_id,
          discountPercentage: data.discount_percentage,
        });
      } catch (error) {
        setResult({
          isValid: false,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to validate code',
          practitionerName: null,
          practitionerId: null,
          codeId: null,
          discountPercentage: null,
        });
      }
    };

    validateCode();
  }, [debouncedCode]);

  return result;
};
